import { useEffect, useMemo, useRef, useState } from "react";

import { AgGridReact } from "ag-grid-react";
import Box from "@mui/material/Box";
import { grey } from "@mui/material/colors";

import { withTranslation } from "react-i18next";
import { debounce, isEmpty } from "lodash";
import { addDays, format } from "date-fns";

import fpPlanResultService from "@plannel/services/fp/fp-plan-result-service";
import VersionSearchGroup from "@plannel/pages/factory-plan/common/component/VersionSearchGroup";
import PeriodSearchGroup, { BUCKET_CASE } from "@plannel/pages/factory-plan/common/component/PeriodSearchGroup";
import SearchOptsGroupRetrieve from "@plannel/pages/factory-plan/common/component/SearchOptsGroupRetrieve";
import FpGridSetting, {
  readHide, createSumAwareFormatter, buildGroupColumnPair, REPORT_TREE_GRID_OPTIONS, buildReportStatusBar,
  applyColumnOrder, buildDayBucketPivotColumnDefs, consumePendingFocus
} from "@plannel/pages/factory-plan/common/grid";
import { buildXAxisSortMap, mergeChartRowsByGroupKey, buildChartDataAndSeries } from "@plannel/pages/factory-plan/common/chart";
import CustomDetailHeader from "@plannel/components/aggrid/CustomDetailHeader";
import { getFilterStore } from "@plannel/components/aggrid/FilterStatusPanel";
import GridUtils from "@plannel/components/aggrid/GridUtils";
import FilterContainer from "@plannel/components/layout/FilterContainer";
import SplitterContainer from "@plannel/components/SplitterContainer";
import PivotChartComposed from "@plannel/components/rechart/PivotChartComposed";
import Snackbar from "@plannel/components/Snackbar";
import { DownloadButton } from "@plannel/components/ActionIconButton";
import ColorUtil from "@plannel/pages/utils/color-util";
import reduxUtil from "@plannel/utils/redux-util";
import debug from "@plannel/utils/debug-util";

const { snackBarMessageColor } = ColorUtil;

// old FP_SRH_RANGE.attr01Val(DB 단일 고정값) 이식 — 버전 선택 시 종료일 = 시작일 + N일.
// 화면/planScope별로 다른 값이 아니라서 JSON 카탈로그 없이 상수 하나로 둔다(가이드 §A-3c).
// TODO(MIG-T2): [planresult/PlanResult] 조회조건 세팅 3종(버킷 목록/검색필드 표시/검색구간)의 테넌트별 커스터마이징 미이관.
//   old: search-option/bucket · search-option/srh-options · setting FP_SRH_RANGE를 각각 DB에서 조회
//   사유: 정적 구성(JSON 카탈로그/고정 카탈로그/상수)으로 전환 — 가이드 §A-3~§A-3d에서 결정
const SEARCH_ZONE_DAYS = 9;

// old SearchOptsGroup이 이 화면에서 실제로 쓰던 필드 2개(loadData/loadChartData의 getValues 기준).
// DB(search_option)로 표시 유무를 제어하지 않고 고정 카탈로그로 항상 노출한다(§A-3b).
const SEARCH_FIELD_LABELS = { resourceCdNm: "FP_RESOURCE", itemCdNm: "FP_ITEM" };
const SEARCH_FIELD_KEYS = Object.keys(SEARCH_FIELD_LABELS);

// 백엔드가 합계 행에 넣는 표식 — 그리드에선 번역해 보여주고, 차트 데이터에선 제외한다(old 동일).
// (현재 `AnalysisController#getPlanResults`는 SUM 행을 만들지 않지만, old가 방어적으로 걸러내던
//  코드라 그대로 유지한다 — 백엔드가 나중에 합계 행을 붙여도 화면이 깨지지 않는다.)
const SUM_ROW = "SUM";

const PARAM_DATE_FORMAT = "yyyyMMdd";

// 트리(행 그룹) 계층 — 이 순서가 곧 그룹 depth다: 공장코드 > 공장명 > 자원코드 > 자원명 > (leaf)품목.
// 코드/명칭을 각각 그룹 레벨로 둔 건 `master-plan/reports/ResourceUtilization.js`(이 코드베이스의
// 그리드+차트 Report 표준, §A-6b)와 동일한 방식 — 코드↔명칭이 1:1이라 depth가 늘어도 행 수는 같다.
const GROUP_FIELDS = ["plantCd", "plantNm", "resourceCd", "resourceNm"];

// 그룹 표시 컬럼의 colId. 명칭 컬럼은 p13n(사용자 컬럼설정 저장) 대상에서 빼야 한다 —
// CustomDetailHeader가 코드 컬럼의 화살표로 보였다/숨겼다 하는 컬럼이라 p13n이 그 가시성을
// 덮어쓰면 토글이 어긋난다(ResourceUtilization의 `*_IGNORE_COL_ID` 선례와 동일).
const PLANT_CD_COL_ID = "plantCdGrp";
const PLANT_NM_COL_ID = `PLANT_GRP_NM${GridUtils.IGNORE_P13N_COLLID}`;
const RESOURCE_CD_COL_ID = "resourceCdGrp";
const RESOURCE_NM_COL_ID = `RESOURCE_GRP_NM${GridUtils.IGNORE_P13N_COLLID}`;
const ITEM_NM_COL_ID = `ITEM_GRP_NM${GridUtils.IGNORE_P13N_COLLID}`;

// CustomDetailHeader가 "코드 컬럼 화살표 → 명칭 컬럼 표시/숨김" 상태를 redux viewState에 저장할 때
// 쓰는 키. 기본값("colsHide")을 그대로 쓰면 3쌍이 같은 키를 공유해 서로 상태를 덮어쓰므로 분리한다.
const PLANT_NM_HIDE_KEY = "plantNmColsHide";
const RESOURCE_NM_HIDE_KEY = "resourceNmColsHide";
const ITEM_NM_HIDE_KEY = "itemNmColsHide";

const PlanResult = ({ t, viewName, title }) => {
  const reduxViewState = reduxUtil.getViewState(viewName);

  const [versionState, setVersionState] = useState({
    planScope: "", planningDate: null, versionCd: "", plantCd: [], selectedVersion: null
  });
  const [periodState, setPeriodState] = useState({
    startDate: null, endDate: null, bucketType: "", isEntirePlanPeriod: true
  });
  // resourceCdNm/itemCdNm — Report는 전체 화면 공통으로 조회 버튼이 없다(§A-9b). 자유 텍스트라
  // 매 키 입력마다 재조회하면 안 되므로 controlled state가 아니라 ref(uncontrolled)로 두고,
  // SearchOptsGroupRetrieve(TextFieldSearch 기반) 안의 Enter/검색아이콘으로만 커밋한다.
  const resourceCdNmRef = useRef("");
  const itemCdNmRef = useRef("");

  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });

  const gridRef = useRef();
  const [rowData, setRowData] = useState([]);

  const [chartData, setChartData] = useState([]);
  const [chartSeries, setChartSeries] = useState([]);
  const [chartLabels, setChartLabels] = useState([]);

  // 응답 순서 뒤바뀜 방지 — 조회(그리드)와 차트는 서로 다른 요청이라 가드도 2개(§0-8).
  const latestGridReqRef = useRef(0);
  const latestChartReqRef = useRef(0);
  // old prevChartParamsRef — 같은 (plant, resource, item) 조합이면 차트를 다시 조회하지 않는다.
  const prevChartParamsRef = useRef(null);
  // 조회 성공 직후 첫 행 기준 focus를 걸 때 쓴다 — setRowData(rows)는 React state 갱신이라
  // 그 자리에서 곧바로 api.setFocusedCell을 부르면 그리드가 새 rowData를 아직 반영하기 전일 수
  // 있다(레이스). "포커스를 걸어야 할 대상"만 여기 적어두고, 그리드가 실제로 새 rowData를
  // 반영한 시점(onRowDataUpdated)에 소비한다.
  const pendingFocusRef = useRef(null);
  // columnDefs를 조회 응답 시점에 다시 만들어 넘기므로(§A-6), 최신 조회조건을 콜백에서 읽기 위한 ref.
  const searchStateRef = useRef({ versionState, periodState });
  searchStateRef.current = { versionState, periodState };

  // planScope→plan-dts→planningDate→versions→versionCd/plantCd→기간 재계산 캐스케이드가
  // 여러 렌더에 걸쳐 순차 커밋되면서 그 각 단계가 독립적으로 자동조회를 트리거해 같은
  // plan-result가 여러 번 호출되는 문제를 막는 디바운스. 실제 호출은 마지막 트리거로부터
  // 200ms 조용히 지난 뒤 searchStateRef.current(그 시점의 최신 상태)를 기준으로 단 한 번만
  // 나간다. 자유 텍스트 수동 커밋(SearchOptsGroupRetrieve)은 사용자의 명시적 동작이라 이
  // 트리거를 쓰지 않고 retrievePlanResult를 직접 호출한다.
  const debouncedRetrieveRef = useRef();
  if (!debouncedRetrieveRef.current) {
    debouncedRetrieveRef.current = debounce(() => retrievePlanResult(), 200);
  }
  useEffect(() => () => debouncedRetrieveRef.current.cancel(), []);

  // 화면을 떠날 때 하단 상태바의 선택 브레드크럼(전역 스토어)을 비운다 — 안 그러면 다음에 이
  // 화면을 열었을 때 이전 선택이 남아 보인다(ResourceUtilization도 동일하게 clear한다).
  useEffect(() => () => getFilterStore(viewName).clear(), [viewName]);

  const defaultGridMemo = useMemo(() => {
    const base = FpGridSetting({ title, viewName });
    return {
      ...base,
      // 조회 전용 화면 — 공용 DefaultGridSetting의 defaultColDef.editable:true를 끈다.
      defaultColDef: { ...base.defaultColDef, editable: false },
      // 합계(SUM) 행 강조 — old styleCallback의 'summary-col' 대응(ResourceUtilization.js의
      // getRowStyle 선례와 동일 방식). 공용 DataState.getRowStyles는 편집 상태 표시용이라
      // 읽기 전용인 이 그리드에선 대체해도 잃는 기능이 없다. 그룹 행은 data가 없어 null이 된다.
      getRowStyle: (params) => (params.data?.itemCd === SUM_ROW ? { backgroundColor: grey[100] } : null),
      // 행 그룹(트리) 3종 세트(common/grid.js REPORT_TREE_GRID_OPTIONS) — `master-plan/reports/
      // ResourceUtilization.js`와 동일한 조합. groupHideOpenParents가 없으면 "공장 행 → 자원 행 →
      // 품목 행"이 각각 별도 행으로 쌓여 행 수가 몇 배로 불어난다 — 켜두면 그룹 값이 자식(품목)
      // 행의 해당 컬럼 자리에 렌더되어, old의 셀 병합과 같은 모양이면서 접기까지 된다.
      ...REPORT_TREE_GRID_OPTIONS,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 'SUM' 행의 코드/명칭 셀은 번역해서 보여준다(old displayCallback 대응, common/grid.js).
  const sumAwareFormatter = createSumAwareFormatter(SUM_ROW, t);

  // 고정(왼쪽 pinned) 컬럼 — old gridItems와 1:1(plantCd/plantNm/resourceCd/resourceNm/itemCd/
  // itemNm/displayColor(hidden)/sum)이되, 구성 방식이 다르다:
  //  - 공장/자원은 **행 그룹(트리)**으로 접고 펼친다(이 코드베이스 Report 표준, §A-6b).
  //    실제 데이터 컬럼은 rowGroup:true + hide:true로 숨기고, 화면에는 showRowGroup 표시 컬럼만 둔다.
  //  - 품목은 트리의 leaf(가장 안쪽 실제 행)라 그룹이 아니라 평범한 field 컬럼이다.
  //  - 코드↔명칭 토글은 공용 CustomDetailHeader(코드 컬럼 헤더의 화살표)로 처리한다
  //    (그룹 표시 컬럼 쌍은 common/grid.js의 buildGroupColumnPair).
  // 왼쪽 고정(pinned:"left")은 여기 나열된 컬럼 전체가 기준 — pivot 컬럼만 가로 스크롤 대상이다.
  const fixedColumnDefs = useMemo(() => [
    // 1) 실제 그룹 기준이 되는 데이터 컬럼 — 화면엔 안 보이고 계층만 만든다(순서=그룹 depth).
    ...GROUP_FIELDS.map(field => ({
      headerName: field, field, colId: field, rowGroup: true, hide: true, suppressColumnsToolPanel: true
    })),

    // 2) 화면에 보이는 그룹 표시 컬럼(코드 + 화살표로 열리는 명칭)
    ...buildGroupColumnPair({
      viewName, hidden: readHide(reduxViewState, PLANT_NM_HIDE_KEY), valueFormatter: sumAwareFormatter,
      cdColId: PLANT_CD_COL_ID, cdField: "plantCd", cdHeader: "FP_PLANT_CD",
      nmColId: PLANT_NM_COL_ID, nmField: "plantNm", nmHeader: "PLANT_NM",
      hideKey: PLANT_NM_HIDE_KEY, width: 150
    }),
    ...buildGroupColumnPair({
      viewName, hidden: readHide(reduxViewState, RESOURCE_NM_HIDE_KEY), valueFormatter: sumAwareFormatter,
      cdColId: RESOURCE_CD_COL_ID, cdField: "resourceCd", cdHeader: "FP_RESOURCE_CD",
      nmColId: RESOURCE_NM_COL_ID, nmField: "resourceNm", nmHeader: "FP_RESOURCE_NM",
      hideKey: RESOURCE_NM_HIDE_KEY, width: 150
    }),

    // 3) leaf(품목) — 그룹이 아니라 실제 행의 값이라 평범한 field 컬럼. 코드/명칭 토글은 동일하게 적용.
    {
      headerName: "FP_ITEM_CD", field: "itemCd", colId: "itemCd", width: 150, pinned: "left", lockPinned: true,
      type: ["nonEditableColumn"], valueFormatter: sumAwareFormatter,
      headerComponent: CustomDetailHeader,
      headerComponentParams: {
        viewName, colsHide: readHide(reduxViewState, ITEM_NM_HIDE_KEY), reduxKey: ITEM_NM_HIDE_KEY, detailColumns: [ITEM_NM_COL_ID]
      }
    },
    {
      headerName: "ITEM_NM", field: "itemNm", colId: ITEM_NM_COL_ID, width: 150, pinned: "left", lockPinned: true,
      hide: readHide(reduxViewState, ITEM_NM_HIDE_KEY), type: ["nonEditableColumn"], valueFormatter: sumAwareFormatter
    },

    // old headerText는 공백 1칸 + visible:false — 차트 색상(displayColor)에 쓰이므로 데이터는 유지한다.
    { headerName: " ", field: "displayColor", colId: "displayColor", hide: true },
    { headerName: "SUM", field: "sum", colId: "sum", width: 100, pinned: "left", lockPinned: true, type: ["numberColumn", "rightAligned"] }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [t]);

  // crosstabHeader("2026-07-20_W30" 또는 일버킷일 때 "2026-07-20_MON")를 "_" 앞(날짜)=그룹,
  // 뒤(주차/월/요일)=자식으로 묶는다. 요일 코드(MON..SUN)면 다국어 처리 + 토/일 강조색까지
  // common/grid.js의 buildDayBucketPivotColumnDefs가 처리한다(§A-6d). GridUtils.setColumnDefs
  // 재정의 후 순서를 다시 못박는 applyColumnOrder도 같은 파일(Report 화면 공통, §A-6c).
  const buildPivotColumnDefs = (crosstabHeader) => buildDayBucketPivotColumnDefs(crosstabHeader, t);

  // old setCrossTabGridDataWithBucket — pivotData[i]를 crosstabHeader[i] 필드로 펼친다.
  const buildRowData = (crosstabHeader, data) => (data ?? []).map(row => {
    const flat = {
      plantCd: row.plantCd, plantNm: row.plantNm,
      resourceCd: row.resourceCd, resourceNm: row.resourceNm,
      itemCd: row.itemCd, itemNm: row.itemNm,
      displayColor: row.displayColor, sum: row.sum
    };
    (crosstabHeader ?? []).forEach((header, index) => {
      flat[header] = row.pivotData?.[index];
    });
    return flat;
  });

  const toParamDate = (date) => (date ? format(date, PARAM_DATE_FORMAT) : null);

  // 조회/차트 공통 파라미터(old searchParams의 공통부). 캐스케이드 중 여러 번 트리거돼도
  // debouncedRetrieveRef가 실제 호출을 마지막 트리거 이후로 늦춰주므로, 실행 시점엔 이미
  // periodState가 최신값으로 커밋돼 있다 — 즉시 조회용 override는 필요 없다.
  const buildBaseParams = () => {
    const { versionState: version, periodState: period } = searchStateRef.current;
    return {
      viewId: viewName,
      subId: "",
      planScope: version.planScope,
      versionCd: version.versionCd,
      resourceCdNm: resourceCdNmRef.current,
      itemCdNm: itemCdNmRef.current,
      bucketType: period.bucketType,
      fromDt: toParamDate(period.startDate),
      toDt: toParamDate(period.endDate),
      usePlanPeriod: !!period.isEntirePlanPeriod
    };
  };

  const clearChart = () => {
    prevChartParamsRef.current = null;
    setChartData([]);
    setChartSeries([]);
    setChartLabels([]);
    getFilterStore(viewName).clear();
  };

  // 하단 상태바 왼쪽의 "지금 보고 있는 위치" 브레드크럼(공용 FilterStatusPanel) 갱신.
  // old의 차트 위 타이틀 텍스트(chartTitle)는 이 패널로 완전히 대체한다 — 이 코드베이스의
  // 그리드+차트 Report 화면 13개(ResourceUtilization/MrpReport/RpReview/BfAccuracyReport 등)
  // 전부 차트 타이틀 없이 FilterStatusPanel 하나로만 "지금 보고 있는 대상"을 보여준다
  // (2026-08-10 확인 — chartTitle류 텍스트를 쓰는 화면 0건). 둘 다 두면 같은 정보가 중복 표시된다.
  //
  // FilterStatusPanel은 화면이 넣어준 스토어 값을 그대로 label/value 칩으로 그리기만 한다.
  // 다만 그 패널이 **미리 정해둔 슬롯 이름**(originLoc/destLoc/customer/item/workCenter/resource)
  // 순서대로 렌더하고, 그 슬롯 이름들은 RP/DP/MP 도메인 어휘라 FP의 공장/자원/품목과 1:1로
  // 대응되지 않는다. 라벨 문구는 우리가 header 값으로 직접 넣으므로 화면에 보이는 결과는 정확하지만,
  // "공장 > 자원 > 품목" 순서를 맞추려면 렌더 순서가 1·2·4번인 슬롯에 실어야 한다
  // (originLoc=공장, destLoc=자원, item=품목). 공용 파일을 고치지 않기 위한 슬롯 매핑이다.
  const updateStatusBreadcrumb = (target) => {
    getFilterStore(viewName).setValue({
      originLocCd: target.plantCd ?? null, originLocCdHeader: t("FP_PLANT_CD"),
      originLocNm: target.plantNm ?? null, originLocNmHeader: t("PLANT_NM"),
      destLocCd: target.resourceCd ?? null, destLocCdHeader: t("FP_RESOURCE_CD"),
      destLocNm: target.resourceNm ?? null, destLocNmHeader: t("FP_RESOURCE_NM"),
      itemCd: target.itemCd ?? null, itemCdHeader: t("FP_ITEM_CD"),
      itemNm: target.itemNm ?? null, itemNmHeader: t("ITEM_NM")
    });
  };

  // 클릭 기준으로 차트를 다시 조회한다. target = { plantCd, plantNm, resourceCd, resourceNm, itemCd, itemNm }
  // (상위 레벨을 클릭하면 하위 코드는 없음 — old updateChartFromGrid와 동일).
  const loadChart = (target) => {
    const { versionState: version } = searchStateRef.current;
    if (!version.planScope || !version.versionCd) return;

    updateStatusBreadcrumb(target);

    const nextParams = {
      plantCd: target.plantCd ?? "", resourceCd: target.resourceCd ?? "", itemCd: target.itemCd ?? ""
    };
    const prev = prevChartParamsRef.current;
    if (prev && prev.plantCd === nextParams.plantCd && prev.resourceCd === nextParams.resourceCd
      && prev.itemCd === nextParams.itemCd) {
      return;
    }
    prevChartParamsRef.current = nextParams;

    const reqId = ++latestChartReqRef.current;
    const params = { ...buildBaseParams(), ...nextParams, useGrouping: "false" };

    // TODO(MIG-T2): [planresult/PlanResult] 그리드 컬럼 필터(값 목록 체크박스) 미이관.
    //   old: plantCd/resourceCd/itemCd에 RealGrid autoFilter, 필터 선택값을 차트 조회의
    //        changes(FilterParameter: plantCds/resourceCds/itemCds)로 함께 전송
    //   사유: 행 그룹(트리)으로 전환하면서 코드 컬럼이 hide:true인 데이터 컬럼이 돼 헤더 필터
    //        아이콘을 띄울 자리가 없어졌다(숨은 컬럼의 필터는 api.showColumnFilter로만 열 수 있고,
    //        그 트리거 UI는 이 코드베이스에 선례가 없다). 백엔드는 changes를 계속 받으므로
    //        2차에 트리거 UI만 얹으면 된다 — 지금은 빈 필터로 보낸다.
    fpPlanResultService.getPlanResultChart(params, {})
      .then(response => {
        if (reqId !== latestChartReqRef.current) return;

        const { chartHeader, data } = response.data ?? {};
        const rows = (data ?? []).filter(r => r.itemCd !== SUM_ROW);

        if (isEmpty(rows) || isEmpty(chartHeader)) {
          setChartData([]);
          setChartSeries([]);
          setChartLabels([]);
          return;
        }

        // PivotChartComposed는 groupKey 값으로 범례를 자동 생성하는데, 그 목록을 data 행과
        // 인덱스로 짝지어 읽는다(PivotChartComposed.js:236-243) → groupKey(itemCd)가 중복되면
        // 값이 어긋난다. 같은 품목이 여러 자원 행으로 나뉘어 올 수 있으므로 먼저 품목 단위로
        // 합산한다(common/chart.js — Report 화면 공통, old도 전부 같은 stack('bar1')에 쌓아
        // 그렸으므로 그래프 결과는 동일하다). 범례(groupKey 값)와 1:1로 맞춘 series — text가
        // 일치해야 막대가 그려지고, typeOptions.fill로 old displayColor(품목별 스와치 색)를
        // 그대로 쓴다(§A-7).
        const merged = mergeChartRowsByGroupKey({ rows, groupKey: "itemCd", chartHeader, displayColorKey: "displayColor" });
        const { chartData, chartSeries: series } = buildChartDataAndSeries({
          merged, groupKey: "itemCd", chartHeader, displayColorKey: "displayColor",
          seriesOptions: { type: "bar", groupName: t("QTY"), stackId: "planResult" }
        });

        setChartLabels(chartHeader);
        setChartData(chartData);
        setChartSeries(series);
      })
      .catch(e => {
        prevChartParamsRef.current = null;
        debug(e);
      });
  };

  const retrievePlanResult = () => {
    const { versionState: version, periodState: period } = searchStateRef.current;

    if (!version.planScope || !version.versionCd) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("FP_MSG_SELECT_VERSION") });
      return;
    }
    if (!period.isEntirePlanPeriod && (!period.startDate || !period.endDate)) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("FP_MSG_SELECT_PLAN_PERIOD") });
      return;
    }

    const reqId = ++latestGridReqRef.current;
    const params = { ...buildBaseParams(), plantCds: version.plantCd };

    // 조회 중 AG Grid 내장 로딩 오버레이 표시(§0-8b 공통 컨벤션) — 저장 아닌 조회(GET)에도
    // 적용하는 것이 지금부터의 표준이다.
    gridRef.current?.api?.setGridOption("loading", true);

    fpPlanResultService.getPlanResults(params)
      .then(response => {
        if (reqId !== latestGridReqRef.current) return;

        const { crosstabHeader, data } = response.data ?? {};
        const api = gridRef.current?.api;
        if (!api) return;

        // 조회할 때마다 기간 버킷이 달라져 컬럼 구성 자체가 바뀐다 → columnDefs를 다시 만들어
        // setColumnDefs로 재정의한다. p13n(컬럼상태 저장)은 이 그리드에 의미가 없어 initState:false(§A-6).
        const nextColumnDefs = [...fixedColumnDefs, ...buildPivotColumnDefs(crosstabHeader)];
        GridUtils.setColumnDefs({ api, columnDefs: nextColumnDefs, viewName, initState: false });
        // setColumnDefs가 되돌려놓은 "이전 조회의 컬럼 순서"를 crosstabHeader 순서로 교정(위 주석 참고).
        applyColumnOrder(api, nextColumnDefs);

        const rows = buildRowData(crosstabHeader, data);
        setRowData(rows);

        if (isEmpty(rows)) {
          clearChart();
          setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoData") });
          return;
        }

        // old: 조회 직후 첫 행의 공장 기준으로 차트를 그리고, 그리드의 현재 선택 셀도 그 행으로
        // 옮겨놓는다(old `gridView.setFocus()` + `setCurrent({itemIndex:1, column:'plantCd'})` —
        // "선택된 셀"과 "차트가 그리는 행"이 항상 같아야 한다는 전제, §A-7 참고).
        // 트리에서 rowIndex 0은 첫 공장 그룹 행이라, 공장 레벨 차트와 정확히 짝이 맞는다.
        pendingFocusRef.current = { rowIndex: 0, colId: PLANT_CD_COL_ID };
        prevChartParamsRef.current = null;
        loadChart({ plantCd: rows[0].plantCd, plantNm: rows[0].plantNm });
      })
      .catch(e => {
        if (reqId === latestGridReqRef.current) clearChart();
        debug(e);
      })
      .finally(() => {
        if (reqId === latestGridReqRef.current) gridRef.current?.api?.setGridOption("loading", false);
      });
  };

  const onGridReady = (params) => {
    GridUtils.setColumnDefs({ ...params, columnDefs: fixedColumnDefs, viewName, initState: false });
  };

  // rowData가 실제로 그리드에 반영된 시점 — pendingFocusRef에 예약해둔 포커스를 여기서 소비한다
  // (retrievePlanResult에서 setRowData 직후 바로 setFocusedCell을 부르면, 그 시점엔 React
  // state 갱신이 아직 그리드에 반영 전이라 레이스가 난다).
  // 조회 성공 직후 예약해둔 focus/선택을 그리드가 새 rowData를 반영한 시점에 소비한다
  // (common/grid.js — Report 화면 공통, §A-7).
  const onRowDataUpdated = () => consumePendingFocus(gridRef, pendingFocusRef);

  // 클릭 컬럼 → 드릴다운 레벨. old updateChartFromGrid의 `column.startsWith('plant'|'resource')`
  // 분기를 트리 표시 컬럼 colId 기준으로 옮긴 것.
  const PLANT_LEVEL_COL_IDS = new Set([PLANT_CD_COL_ID, PLANT_NM_COL_ID]);
  const RESOURCE_LEVEL_COL_IDS = new Set([RESOURCE_CD_COL_ID, RESOURCE_NM_COL_ID]);

  // 클릭한 셀에서 차트를 그릴 드릴다운 대상을 뽑는다(old updateChartFromGrid의 트리 판).
  // groupHideOpenParents 때문에 두 경우를 모두 다뤄야 한다:
  //  - 그룹 행(= 접힌 그룹의 대표 행): 자기 자신부터 부모 방향으로 올라가며 각 그룹 레벨의
  //    (field, key)를 모은다 — 조상 체인에 있는 필드가 곧 그 행이 대표하는 범위다.
  //  - leaf(품목) 행: 그룹이 펼쳐져 있으면 부모 행이 감춰지고 공장/자원 값이 이 행에 렌더되므로,
  //    old처럼 **클릭한 컬럼**으로 레벨을 정한다(공장 칸 클릭 → 공장 차트, 자원 칸 → 자원 차트).
  const resolveChartTarget = (params) => {
    const node = params.node;
    if (!node) return null;

    if (node.group) {
      const target = {};
      for (let current = node; current; current = current.parent) {
        if (current.field) target[current.field] = current.key;
      }
      return isEmpty(target) ? null : target;
    }

    const row = node.data;
    if (!row) return null;

    const colId = params.column?.getColId() ?? "";
    if (PLANT_LEVEL_COL_IDS.has(colId)) {
      return { plantCd: row.plantCd, plantNm: row.plantNm };
    }
    if (RESOURCE_LEVEL_COL_IDS.has(colId)) {
      return { plantCd: row.plantCd, plantNm: row.plantNm, resourceCd: row.resourceCd, resourceNm: row.resourceNm };
    }
    return row;
  };

  // 셀(그룹 행/품목 행 어느 쪽이든) 클릭 시 그 행이 대표하는 범위로 차트를 다시 그린다.
  // AG Grid는 클릭한 셀을 기본적으로 focus로 잡아주므로, 이 클릭이 곧 "현재 선택 셀"이 된다.
  const onCellClicked = (params) => {
    const target = resolveChartTarget(params);
    if (target) loadChart(target);
  };

  // 하단 상태바 — 왼쪽은 지금 선택한 위치의 브레드크럼(공용 FilterStatusPanel), 가운데는 AG Grid
  // 내장 전체 행 수, 오른쪽은 내장 집계(셀 범위 선택 시 합계/평균 등). Report 화면 공통 구성
  // (common/grid.js buildReportStatusBar).
  const statusBar = useMemo(() => buildReportStatusBar(viewName), [viewName]);

  // 버전이 바뀌면 조회 구간을 다시 계산한다(old setVersionPeriod). 그룹을 넘나드는 로직이라
  // PeriodSearchGroup이 아니라 화면이 소유한다(§A-1b). versionCd가 바뀌면 이 effect가 항상
  // periodState.startDate/endDate도 같이 갱신해준다는 게, 아래 자동조회 effect가 versionCd를
  // 직접 안 봐도 되는 전제다(§A-9b — 이중 조회 방지).
  useEffect(() => {
    const version = versionState.selectedVersion;
    if (!version?.startTs) {
      // 버전이 없어지면(plan-dts/versions가 빈 응답이라 VersionSearchGroup이 리셋한 경우)
      // 조회 구간도 이전 값을 들고 있지 않고 같이 비운다.
      setPeriodState(prev => (prev.startDate || prev.endDate) ? { ...prev, startDate: null, endDate: null } : prev);
      return;
    }

    const startDate = new Date(version.startTs);
    const endDate = addDays(startDate, SEARCH_ZONE_DAYS);
    setPeriodState(prev => ({ ...prev, startDate, endDate }));

    // debouncedRetrieveRef로 넘긴다 — 실제 조회는 마지막 트리거로부터 200ms 뒤에 나가므로,
    // 그 시점엔 방금 setPeriodState한 값이 이미 커밋돼 있다(레이스 없음, 위 debouncedRetrieveRef
    // 선언부 주석 참고). 아래 "자동조회" effect도 같은 캐스케이드 안에서 겹쳐 호출하지만
    // 디바운스가 하나로 합쳐준다.
    debouncedRetrieveRef.current();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [versionState.selectedVersion]);

  // PlanScope를 바꿨는데 하위(버전/기간)까지 빈 값으로 리셋된 경우(=새 계획범위에 plan-dts/
  // versions 결과가 없는 경우), 그리드·차트에 이전 계획범위의 조회 결과가 그대로 남아있으면
  // 안 된다 — versionCd가 비면 같이 비운다. versionCd가 채워지는 경로(정상 조회)는 위/아래
  // effect가 이미 재조회를 트리거하므로 여기서는 "없어진" 경우만 처리한다.
  useEffect(() => {
    if (versionState.versionCd) return;

    // 리셋 직전에 예약돼 있을 수 있는 디바운스 호출을 취소한다 — 안 그러면 리셋 이후 지연
    // 실행된 retrievePlanResult가 "버전을 선택해 주세요" 스낵바를 잘못 띄운다.
    debouncedRetrieveRef.current.cancel();

    const api = gridRef.current?.api;
    if (api) {
      GridUtils.setColumnDefs({ api, columnDefs: fixedColumnDefs, viewName, initState: false });
      applyColumnOrder(api, fixedColumnDefs);
    }
    setRowData([]);
    clearChart();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [versionState.versionCd]);

  // 화면 전체 조회 버튼을 없앤 대신, 선택형 조건(버전/plant/기간/버킷)이 바뀌면 값이 준비되는
  // 즉시 자동 조회한다(§A-9b — Master의 planScope 즉시조회, `OrderTracking.js`의
  // 선택형 필터 즉시조회 선례와 동일 원칙). 자유 텍스트(resourceCdNm/itemCdNm)는 여기서 안
  // 다룬다 — SearchOptsGroupRetrieve 내부의 수동 커밋(Enter/검색아이콘)으로만 조회된다.
  //
  // versionCd 변경은 이 effect가 직접 보지 않는다 — versionCd가 바뀌면 위 effect가 항상
  // periodState.startDate/endDate를 같이 갱신하고 그 값으로 debouncedRetrieveRef를 호출한다.
  // 이 effect는 periodState.startDate 변경으로 뒤따라 한 번 더 실행되어 같은 트리거를 겹쳐
  // 호출하는데, debouncedRetrieveRef가 마지막 트리거 이후 200ms 뒤 딱 한 번만 실제 조회를
  // 실행해주므로 캐스케이드 중 여러 번 겹쳐도 plan-result는 한 번만 나간다(§A-9b 참고 — 최초
  // 진입 캐스케이드로 버전이 처음 잡힐 때도 이 경로로 자동조회가 이뤄진다).
  useEffect(() => {
    const version = searchStateRef.current.versionState;
    const period = searchStateRef.current.periodState;
    if (!version.planScope || !version.versionCd) return;
    if (!period.isEntirePlanPeriod && (!period.startDate || !period.endDate)) return;

    debouncedRetrieveRef.current();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    periodState.startDate, periodState.endDate, periodState.bucketType, periodState.isEntirePlanPeriod,
    versionState.plantCd
  ]);

  // PivotChartComposed는 x축 카테고리를 기본적으로 문자열 사전순 정렬한다(pivotXAxis.sort()) —
  // 주(W33..)/월(09, 10) 라벨이 섞이면 "09" < "10" < "W33"라 실제 시간 순서(주가 월보다 앞)와
  // 어긋난다(2026-08-10 실제로 이 문제로 발견). 공용 파일은 고치지 않고, 이미 있는 확장 포인트
  // (pivotConfig.xAxisSortMap — 있으면 그 맵 순서로, 없으면 기본 정렬로 떨어짐)를 chartLabels
  // (=백엔드가 내려준 원래 순서, §A-6d 계열) 기준으로 채워서 정렬을 덮어쓴다(common/chart.js —
  // Report 화면 공통, 모든 크로스탭 차트 화면이 이 함수를 pivotConfig.xAxisSortMap에 넣는다).
  const pivotConfig = useMemo(() => ({
    groupKey: "itemCd",
    chartSeries,
    include: chartLabels,
    xAxisSortMap: buildXAxisSortMap(chartLabels)
  }), [chartSeries, chartLabels]);

  return (
    <>
      {/* hasIcons 기본값(true) 유지 — FilterContainer는 children 배열의 "마지막 요소"를 필터
          접기/펼치기 아이콘 바로 왼쪽에 고정 배치한다(다른 화면과 동일 관례, Route.js 참고).
          여기선 그 마지막 요소가 DownloadButton을 담은 Box라 엑셀 내보내기 버튼이 자동으로 그
          자리에 온다 — hasIcons={false}를 주면 그 자리에 빈 슬롯이 대신 들어가고 DownloadButton은
          접히는 필터 영역 안으로 밀려난다(2026-08-10 발견·수정, §0-8). */}
      <FilterContainer>
        <VersionSearchGroup
          value={versionState}
          onChange={(partial) => setVersionState(prev => ({ ...prev, ...partial }))}
        />
        <PeriodSearchGroup
          value={periodState}
          onChange={(partial) => setPeriodState(prev => ({ ...prev, ...partial }))}
          // Report 화면 bucketCase 배정표(fp-report-screen-guide.md §A-3) — ResProdPlan/
          // ResrcUtilization 2개 화면만 WITHOUT_HOUR, 그 외 전부(PlanResult 포함) WITH_HOUR.
          bucketCase={BUCKET_CASE.WITH_HOUR}
        />
        {/* 조회 버튼 없음(§A-9b, Report 화면 공통 정책) — 자유 텍스트는 필드 안의 Enter/검색아이콘으로
            수동 커밋(TextFieldSearch 기반, 위 VersionSearchGroup/PeriodSearchGroup은 선택형이라 값이
            바뀌는 즉시 자동조회된다). */}
        <SearchOptsGroupRetrieve
          fieldRefs={{ resourceCdNm: resourceCdNmRef, itemCdNm: itemCdNmRef }}
          fieldKeys={SEARCH_FIELD_KEYS}
          labels={SEARCH_FIELD_LABELS}
          retrieve={retrievePlanResult}
        />
        <Box sx={{ display: "flex" }}>
          <DownloadButton
            onClick={() => gridRef.current?.api?.exportDataAsExcel({ fileName: `${t(title)}.xlsx`, sheetName: t(title) })}
          />
        </Box>
      </FilterContainer>
      {/* old VerticalGridChartBox의 sizes={[50, 50]}(direction="vertical") 대응 — SplitterContainer의
          기본 initialSizes([3, 2], 60/40)를 그대로 두면 그리드:차트가 1:1이 아니게 된다(§A-1c). */}
      <SplitterContainer initialSizes={[1, 1]}>
        {/* split bar와 그리드 사이에 여백이 아예 없어서 붙어 보이는 문제(2026-08-10) — 그리드/차트
            양쪽 다 split bar에 붙는 쪽에 8px 여백을 준다. height:100%에 그대로 padding을 주면
            부모(SplitterContainer가 지정한 pane 높이)를 넘칠 수 있어, margin + calc로 뺀다. */}
        <Box id="fp-plan-result-grid" className="ag-theme-balham grid-area" sx={{ height: "calc(100% - 8px)", pb: 1 }}>
          <AgGridReact
            ref={gridRef}
            rowData={rowData}
            {...defaultGridMemo}
            statusBar={statusBar}
            onGridReady={onGridReady}
            onRowDataUpdated={onRowDataUpdated}
            onCellClicked={onCellClicked}
          />
        </Box>
        {/* className="chart-area"(App.scss)는 overflow:auto + 스크롤바 숨김 조합이라 이 화면의
            고정 스플리터 영역엔 안 맞는다(아래 차트 Box 주석 참고) — sx의 overflow:hidden으로
            그 클래스의 overflow:auto를 화면 범위에서 덮어써 "안 보이는 스크롤"을 원천 차단한다. */}
        <Box className="chart-area" sx={{ height: "calc(100% - 8px)", mt: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
          {/* old는 차트 위에 타이틀 텍스트(chartTitle)를 그렸지만, 이 코드베이스의 다른 그리드+차트
              Report 화면(ResourceUtilization/MrpReport 등 13개)은 전부 그 정보를 하단 statusBar의
              FilterStatusPanel 브레드크럼으로만 보여주고 별도 차트 타이틀은 쓰지 않는다 —
              2026-08-10 확인 후 그 컨벤션에 맞춰 제거(§A-7). */}
          {/* old는 이 차트에 ChartLegendPlugin을 걸지 않아 범례가 없었다(품목을 버킷 헤더별로
              쌓아 보여주는 형태라 범례가 오히려 방해됨) — PivotChartComposed(공용 파일)엔 범례를
              끄는 prop이 없어서 recharts Legend가 렌더하는 `.recharts-legend-wrapper`를
              화면 스코프 CSS로만 숨긴다(공용 파일 수정 없이 old와 동일한 결과). */}
          {/* App.scss의 전역 `.chart-area` 클래스가 overflow:auto + scrollbar-width:none으로
              "스크롤은 되는데 스크롤바만 숨김" 처리라(다른 화면의 의도적 관례), 이 화면처럼 스플리터로
              고정된 영역엔 안 맞는다 — PivotChartComposed 내부 ResponsiveContainer의
              `chartOptions.minHeight` 기본값(240px)이 분할 영역보다 커지면 차트가 박스 밖으로
              넘치고, 그 넘친 부분이 안 보이는 스크롤로 잡혀 "스크롤바 없이 스크롤되는" 증상이
              됐다(2026-08-10). overflow:hidden으로 새는 것 자체를 막고, minHeight를 낮춰 애초에
              넘치지 않게 한다(공용 파일 수정 없이 `options` prop으로 기본값 오버라이드). */}
          <Box sx={{ flex: 1, minHeight: 0, overflow: "hidden", ".recharts-legend-wrapper": { display: "none" } }}>
            {/* TODO(MIG-T2): [planresult/PlanResult] 차트 장식성 인터랙션 3종 미이관.
                  old: ChartHatchPatternPlugin(빗금), highlightChartByLabel/resetChartHighlighting(hover dim),
                       ChartDataLabelPlugin(막대 위 수치)
                  사유: recharts/PivotChartComposed에 대응 훅이 없고, 1차 목표(조회·표시)에 불필요 — 가이드 §A-7에서 Tier2 확정 */}
            <PivotChartComposed
              pivotConfig={pivotConfig}
              data={chartData}
              options={{ tooltipContentStyle: { fontSize: "12px" }, minHeight: "0px", decimalPoint: true }}
              showConfigurationPopup={false}
              nullConvert
            />
          </Box>
        </Box>
      </SplitterContainer>
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
    </>
  );
};

export default withTranslation()(PlanResult);
